import React, { useEffect, useState } from "react";
import axios from "axios";

/**
 * Host-facing Live Reward progress card (Feature #2, OLD ADMIN #1's
 * non-conflicting "Host can see reward progress" requirement).
 *
 * Reused inside the existing Host Panel home page (Admin.js) instead of a
 * new/unrelated screen. All numbers come straight from the server progress
 * API — nothing here is calculated on the frontend, so a modified client can
 * never fake progress or eligibility.
 */
const LiveRewardProgress = ({ UI, hostId }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Client correction (Phase 4) — "Monthly Total Valid Day / Valid Time".
  // Fetched separately from the cycle progress above: this is month-scoped
  // (resets on the 1st at 06:00 Bangladesh time) rather than cycle-scoped
  // (resets whenever a reward is granted) — the two numbers can legitimately
  // differ, so they are shown as two clearly separate rows, not merged.
  // Failing to load this never hides the existing cycle progress above.
  const [monthlyStats, setMonthlyStats] = useState(null);

  useEffect(() => {
    if (!hostId) return;
    setLoading(true);
    axios
      .get(`liveReward/progress?userId=${hostId}`)
      .then((res) => {
        const body = res.data;
        if (body?.status) {
          setProgress(body);
          setError("");
        } else {
          setProgress(null);
          setError(body?.message || "Live Reward is not available yet.");
        }
      })
      .catch(() => setError("Could not load Live Reward progress."))
      .finally(() => setLoading(false));
  }, [hostId]);

  useEffect(() => {
    if (!hostId) return;
    axios
      .get(`liveReward/monthlyStats?userId=${hostId}`)
      .then((res) => {
        const body = res.data;
        if (body?.status) setMonthlyStats(body);
      })
      .catch(() => {});
  }, [hostId]);

  const formatMinutes = (totalSeconds) => {
    const seconds = Math.max(0, Number(totalSeconds) || 0);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const cardShell = (children) => (
    <div
      className="mt-4"
      style={{
        border: `1px solid #ffffff4d`,
        borderRadius: 16,
        overflow: "hidden",
        backdropFilter: "blur(2px)",
        margin: "0px 10px",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center px-3 py-3"
        style={{ background: UI.cardBgTop }}
      >
        <h3
          className="mt-1"
          style={{ fontWeight: 600, color: UI.accent, fontSize: 16, letterSpacing: 0.2, marginBottom: 0 }}
        >
          🎁 Live Reward
        </h3>
      </div>
      <div style={{ background: UI.cardBg }}>{children}</div>
    </div>
  );

  const row = (label, value, valueColor) => (
    <div
      className="d-flex justify-content-between align-items-center px-3 py-3"
      style={{ borderTop: `1px solid ${UI.cardEdge}` }}
    >
      <h3 className="mt-1 mb-0" style={{ fontWeight: 400, fontSize: 14, color: "#DCC8E8" }}>
        {label}
      </h3>
      <p className="mt-1 mb-0" style={{ fontWeight: 600, color: valueColor || UI.accent, fontSize: 15 }}>
        {value}
      </p>
    </div>
  );

  if (loading) {
    return cardShell(
      <div className="px-3 py-3" style={{ color: "#DCC8E8", fontSize: 14 }}>
        Loading...
      </div>
    );
  }

  if (error || !progress) {
    return cardShell(
      <div className="px-3 py-3" style={{ color: "#DCC8E8", fontSize: 14 }}>
        {error || "Live Reward is not available yet."}
      </div>
    );
  }

  const hostTypeLabel = progress.hostType === "audio" ? "Audio Host" : "Video Host";
  const status = progress.isEligible
    ? "Eligible — reward will be credited automatically"
    : "In Progress";

  return cardShell(
    <>
      {row("Host Type", hostTypeLabel)}
      {row(
        "Today's Live Time",
        `${formatMinutes(progress.todaySeconds)} / ${formatMinutes(progress.requiredSecondsPerDay)}`
      )}
      {progress.requiredContinuousSeconds > 0 &&
        row(
          "Longest Session Today",
          `${formatMinutes(progress.todayLongestSessionSeconds)} / ${formatMinutes(progress.requiredContinuousSeconds)} needed`,
          progress.todayContinuousSatisfied ? "#4ADE80" : "#F87171"
        )}
      {row("Eligible Days", `${progress.eligibleDays} / ${progress.requiredDays}`)}
      {row("Total Qualifying Time", formatMinutes(progress.qualifyingSeconds))}
      {row("Reward Amount", `${progress.rewardDiamond} 🪙`)}
      {row("Status", status, progress.isEligible ? "#4ADE80" : UI.accent)}

      {/* Client correction (Phase 4) — Monthly Total Valid Day / Valid Time.
          Month-scoped (resets on the 1st at 06:00 Bangladesh time), distinct
          from the cycle progress above (resets when a reward is granted). */}
      {monthlyStats && monthlyStats.eligible && (
        <>
          <div
            className="px-3 pt-3"
            style={{ borderTop: `1px solid ${UI.cardEdge}`, fontSize: 12, color: "#9C7BAE", fontWeight: 600, letterSpacing: 0.3 }}
          >
            THIS MONTH
          </div>
          {row("Monthly Total Valid Day", `${monthlyStats.eligibleDays} day${monthlyStats.eligibleDays === 1 ? "" : "s"}`)}
          {row("Monthly Total Valid Time", formatMinutes(monthlyStats.qualifyingSeconds))}
        </>
      )}
    </>
  );
};

export default LiveRewardProgress;
